/**
 * 화면 0 · 모드 선택 (ChooseScreen) — 테이블/픽업 둘 다 제공되는 매장 진입점.
 *
 * 일시정지된 큐도 운영 중인 큐이므로 카드는 유지하되 선택만 막고 안전한 enum 사유를
 * 안내한다. 기능 자체가 unavailable 인 큐는 부모가 이 화면에 전달하지 않는다(#809).
 *
 * @see #781 / admin#2111 설계 §7 / task-9 브리프
 */
import type { ReactNode } from 'react';
import { ChevronRight, ShoppingBag, Utensils } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { Locale } from '@/config/locales';
import {
  isWaitlistQueueOpen,
  type WaitlistQueueType,
  type WaitlistStatusData,
} from '@/lib/api/waitlist';
import { getWaitlistPauseCopy, getWaitlistPauseReasonText } from './pauseCopy';
import { WaitlistAppBar } from './WaitlistParts';
import type { WaitlistStrings } from './strings';

interface WaitlistChooseScreenProps {
  t: WaitlistStrings;
  storeName: string;
  storeZone: string;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  storeMeta?: WaitlistStatusData['store_meta'];
  /** 큐별 신규 접수 현황. 없으면 구계약 호환상 두 카드 모두 open 으로 취급한다. */
  queues?: WaitlistStatusData['queues'];
  onChoose: (mode: WaitlistQueueType) => void;
}

interface ChooseOption {
  mode: WaitlistQueueType;
  icon: ReactNode;
  title: string;
  desc: string;
  emphasis: 'primary' | 'secondary';
}

export function WaitlistChooseScreen({
  t,
  storeName,
  storeZone,
  locale,
  onLocaleChange,
  storeMeta,
  queues,
  onChoose,
}: WaitlistChooseScreenProps) {
  const pauseCopy = getWaitlistPauseCopy(locale);
  const options: ChooseOption[] = [
    {
      mode: 'table',
      icon: <Utensils className="size-[26px]" />,
      title: t.modeTable,
      desc: t.modeTableDesc,
      emphasis: 'primary',
    },
    {
      mode: 'pickup',
      icon: <ShoppingBag className="size-[26px]" />,
      title: t.modePickup,
      desc: t.modePickupDesc,
      emphasis: 'secondary',
    },
  ];

  return (
    <>
      <WaitlistAppBar
        storeName={storeName}
        subtitle={storeZone}
        locale={locale}
        onLocaleChange={onLocaleChange}
        languageLabel={t.idioma}
        logoUrl={storeMeta?.logo_url}
        mapsUrl={storeMeta?.maps_url}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="px-5 pb-10 pt-3">
          <Badge variant="secondary" className="mb-3">
            {t.filaAbierta}
          </Badge>
          <h1 className="m-0 mb-1 text-[26px] font-extrabold leading-tight tracking-[-0.02em]">
            {t.chooseTitle}
          </h1>
          <p className="m-0 mb-[22px] text-sm leading-relaxed text-muted-foreground">
            {t.chooseLead}
          </p>
          <div className="flex flex-col gap-3">
            {options.map((option) => {
              const queue = queues?.[option.mode];
              const open = queue ? isWaitlistQueueOpen(queue) : true;

              return (
                <button
                  key={option.mode}
                  type="button"
                  aria-disabled={!open}
                  onClick={() => {
                    if (open) {
                      onChoose(option.mode);
                    }
                  }}
                  className="flex items-center gap-4 rounded-xl border border-border bg-card p-[18px] text-start aria-disabled:cursor-not-allowed aria-disabled:opacity-65"
                >
                  <span
                    className={`grid size-[54px] shrink-0 place-items-center rounded-[15px] text-white ${
                      option.emphasis === 'primary' ? 'bg-primary' : 'bg-secondary'
                    }`}
                  >
                    {option.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[17px] font-bold">{option.title}</span>
                    <span className="mt-0.5 block text-[13px] leading-snug text-muted-foreground">
                      {open ? option.desc : pauseCopy.badge}
                    </span>
                    {!open && (
                      <span className="mt-1 block text-xs leading-snug text-destructive-dark">
                        {getWaitlistPauseReasonText(locale, queue?.pause_reason)}
                      </span>
                    )}
                  </span>
                  {open && (
                    <span className="shrink-0 text-muted-foreground">
                      {/* RTL(아랍어 등)에서는 "다음/진행" 방향이 왼쪽이어야 한다 —
                          아이콘만 좌우 반전한다(#794 리뷰). */}
                      <ChevronRight className="size-5 rtl:scale-x-[-1]" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
